import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Button,
  Typography,
  Space,
  message,
  Spin,
  Modal,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import axiosInstance from "../../../../configs";
import { canEdit, MODULES } from "../../../../utils/rbac";

const { Text } = Typography;
const { Option } = Select;

const DOC_OPTIONS = [
  { label: "BC 2.7", value: 27 },
  { label: "BC 2.8", value: 28 },
  { label: "BC 3.3", value: 33 },
  { label: "P3BET", value: "P3BET" },
];

const NPWP = "063239834304400";

export default function FormEditPengeluaranBarang() {
  const [form] = Form.useForm();
  const { idOutgoing } = useParams();

  const [pageLoading, setPageLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  const [headerData, setHeaderData] = useState(null);
  const [originalDetails, setOriginalDetails] = useState([]);

  // Untuk dropdown Nopen (kalau user mau ganti nopen)
  const [nopenOptions, setNopenOptions] = useState([]);
  const [rawNopen, setRawNopen] = useState([]);
  const [loadingNopen, setLoadingNopen] = useState(false);

  // Watch items untuk total kemasan
  const itemsWatch = Form.useWatch("items", form);
  const totalBarangKeluar = useMemo(() => {
    if (!itemsWatch || !Array.isArray(itemsWatch)) return 0;
    return itemsWatch.reduce(
      (sum, row) => sum + (Number(row?.jumlahKeluar) || 0),
      0,
    );
  }, [itemsWatch]);

  // ----------- LOAD DATA AWAL (VIEW MODE) -------------
  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);
      try {
        const res = await axiosInstance.get(
          `/outgoing/${idOutgoing}/with-details`,
        );

        const header = res.data?.header || {};
        const details = res.data?.details || [];

        setHeaderData(header);
        setOriginalDetails(details);

        // Map details ke format form
        // Kita perlu fetch info barang tambahan (stok saat ini) untuk validasi
        // Tapi karena ini edit, kita asumsikan stok saat ini = stok di DB + qty yang sedang di-hold di detail ini
        // Untuk simplifikasi, kita load detail apa adanya dulu.
        // Idealnya kita fetch stok terkini juga.

        const mappedItems = await Promise.all(
          details.map(async (d) => {
            // Fetch stok terkini untuk item ini
            let currentStock = 0;
            let idItemStock = null;
            try {
              const stockRes = await axiosInstance.get("/item-stock", {
                params: { item_code: d.item_code },
              });
              const stockList = stockRes.data?.data || [];
              // Cari yang lot & nopen sama
              const match = stockList.find(
                (s) => s.lokasi_lot === d.lot && s.nopen === d.nopen_asal,
              );
              if (match) {
                currentStock = match.jumlah_kemasan;
                idItemStock = match.id_item_stock;
              }
            } catch (e) {
              console.error("Gagal fetch stok", e);
            }

            // Stok yang bisa dipakai = stok di gudang + stok yang sedang dipakai di dokumen ini
            const availableStock =
              currentStock + (Number(d.total_kemasan) || 0);

            return {
              id_outgoing_detail: d.id_outgoing_detail,
              namaBarang: d.item_code, // atau nama barang kalau ada
              nopenAsal: d.nopen_asal,
              nomorAjuAsal: d.nomor_aju_asal || null,
              seri: d.seri,
              jumlahKeluar: d.total_kemasan,
              lot: d.lot,
              stokSaatIni: availableStock, // Stok visual
              kodeBarang: d.item_code,
              kodeJenisKemasan: d.kemasan,
              kodeSatuanBarang: d.satuan || null,
              jumlahSatuan: d.total_satuan || 0,
              idItemStock: idItemStock,
              id_customer: d.id_customer,
            };
          }),
        );

        form.setFieldsValue({
          delivery_number: header.delivery_number,
          delivery_date: header.delivery_date
            ? dayjs(header.delivery_date)
            : null,
          id_document: header.id_document || undefined,
          document_number: header.document_number,
          document_date: header.document_date
            ? dayjs(header.document_date)
            : null,
          owner_name: header.nama_pemilik || header.id_contact || "",
          container_number: header.container_number || "", // kalau ada
          plat_number: header.plat_number || "",
          items: mappedItems,
        });
      } catch (err) {
        console.error(err);
        message.error("Gagal mengambil data pengeluaran.");
      } finally {
        setPageLoading(false);
      }
    };

    if (idOutgoing) fetchData();
  }, [idOutgoing, form]);

  const handleCancel = () => {
    window.history.back();
  };

  // ==== AMBIL LIST NOPEN (Kalau user mau ganti dokumen) ==== //
  const handleJenisDokumenChange = async (kodeDokumen) => {
    // Reset field terkait dokumen
    form.setFieldsValue({
      document_number: undefined,
      document_date: null,
      owner_name: undefined,
      // items: [], // Jangan reset items kalau user cuma ganti jenis dok tapi batal
    });
    setNopenOptions([]);

    if (!kodeDokumen) return;

    setLoadingNopen(true);
    try {
      const res = await axiosInstance.get("/portal/pasifik/get-data-nopen", {
        params: {
          npwp: NPWP,
          kodeDokumen,
          statusDokumen: "SPPB",
        },
      });

      const list = res.data?.data || [];
      setRawNopen(list);
      setNopenOptions(
        list.map((item) => ({
          label: `${item.nomorDaftar} - ${item.tanggalDaftar}`,
          value: item.nomorDaftar,
        })),
      );
    } catch (err) {
      console.error(err);
      message.error("Gagal mengambil daftar No Dokumen Pabean");
    } finally {
      setLoadingNopen(false);
    }
  };

  const handleNopenChange = async (nomorDaftar) => {
    // Logic sama dengan create, populate header info
    const header = rawNopen.find((d) => d.nomorDaftar === nomorDaftar);
    if (!header) return;

    const entitas = header.entitas || [];
    const ownerEntitas = entitas[0] || null;
    const ownerName = ownerEntitas?.namaEntitas || "";
    const tanggalDaftar = header.tanggalDaftar || null;

    form.setFieldsValue({
      document_date: tanggalDaftar ? dayjs(tanggalDaftar) : null,
      owner_name: ownerName,
    });

    // Note: Di edit mode, kita mungkin tidak mau otomatis replace items
    // karena user mungkin cuma mau ganti nomor dokumen tapi barangnya sama (kasus revisi)
    // Jadi kita biarkan items apa adanya, user yang harus sesuaikan manual jika perlu.
  };

  // ----------- SUBMIT (UPDATE) -------------
  // ----------- SUBMIT (UPDATE) -------------
  const onFinish = async (values) => {
    if (!headerData) return;

    const items = values.items || [];
    if (!items.length) {
      message.error("Tidak ada barang yang akan dikeluarkan");
      return;
    }

    // Filter items yang jumlahKeluar > 0
    const validItems = items.filter(
      (row) => (Number(row.jumlahKeluar) || 0) > 0,
    );
    if (validItems.length === 0) {
      message.error("Tidak ada barang yang akan dikeluarkan (jumlah > 0)");
      return;
    }

    // Validasi stok
    const invalid = validItems.find(
      (row) =>
        row.lot !== "LS.99" &&
        Number(row.jumlahKeluar || 0) > Number(row.stokSaatIni || 0),
    );
    if (invalid) {
      message.error(
        `Stok tidak cukup untuk item ${invalid.namaBarang} (Lot: ${invalid.lot}). Stok tersedia: ${invalid.stokSaatIni}`,
      );
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;
    const items = values.items || [];
    // Re-filter valid items
    const validItems = items.filter(
      (row) => (Number(row.jumlahKeluar) || 0) > 0,
    );

    try {
      setSaving(true);

      const totalKemasan = validItems.reduce(
        (sum, row) => sum + (Number(row.jumlahKeluar) || 0),
        0,
      );

      // Header Payload
      const headerPayload = {
        ...headerData,
        id_document: values.id_document,
        delivery_number: values.delivery_number,
        delivery_date: values.delivery_date
          ? dayjs(values.delivery_date).format("YYYY-MM-DD")
          : null,
        plat_number: values.plat_number || null,
        document_number: values.document_number,
        document_date: values.document_date
          ? dayjs(values.document_date).format("YYYY-MM-DD")
          : null,
        total_packaging: totalKemasan,
        updated_by: 0,
        updated_at: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      };

      // Exclude fields yang tidak perlu dikirim balik atau read-only di BE
      delete headerPayload.id_outgoing;
      delete headerPayload.nama_pemilik;
      delete headerPayload.nama_pemasok;
      delete headerPayload.created_at;

      // Details Payload
      const detailsPayload = validItems.map((row) => ({
        id_outgoing_detail: 0, // BE akan hapus lama & insert baru, jadi ID 0 ok
        id_outgoing: headerData.id_outgoing,
        id_customer:
          row.id_customer || String(headerData.id_contact).slice(0, 16), // Fallback ke header contact
        nopen_asal: row.nopenAsal,
        nomor_aju_asal: row.nomorAjuAsal || null,
        seri: row.seri,
        item_code: row.kodeBarang,
        kemasan: row.kodeJenisKemasan,
        total_kemasan: Number(row.jumlahKeluar) || 0,
        satuan: row.kodeSatuanBarang || null,
        total_satuan: Number(row.jumlahSatuan) || 0,
        lot: row.lot,
        created_by: 0,
        updated_by: 0,
      }));

      await axiosInstance.put(`/outgoing/${idOutgoing}/with-details`, {
        header: headerPayload,
        details: detailsPayload,
      });

      message.success("Pengeluaran barang berhasil diupdate.");
      setIsEditing(false);

      // Reload page data
      // window.location.reload(); // Atau fetch ulang
      // Fetch ulang manual:
      setPageLoading(true);
      const res = await axiosInstance.get(
        `/outgoing/${idOutgoing}/with-details`,
      );
      const newHeader = res.data?.header || {};
      const newDetails = res.data?.details || [];
      setHeaderData(newHeader);
      setOriginalDetails(newDetails);
      // Re-map items... (bisa refactor fetch logic ke function terpisah)
      // Untuk quick fix, reload window saja atau redirect back
      window.location.reload();

      setConfirmVisible(false);
    } catch (err) {
      console.error(err);
      message.error(
        err.response?.data?.error || "Gagal mengupdate pengeluaran barang.",
      );
    } finally {
      setSaving(false);
      setPageLoading(false);
    }
  };

  const handleCancelEdit = () => {
    window.location.reload(); // Reset form paling gampang reload
  };

  return (
    <Card className="kt-portlet" bodyStyle={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Typography.Title level={3} style={{ marginBottom: 4 }}>
            Detail Pengeluaran Barang
          </Typography.Title>
          <Text type="secondary">
            Lihat dan perbarui data pengeluaran barang.
          </Text>
        </Col>
        <Col>
          <Space>
            {!isEditing && canEdit(MODULES.INVENTORY) && (
              <Button
                icon={<EditOutlined />}
                onClick={() => setIsEditing(true)}
                htmlType="button"
              >
                Edit
              </Button>
            )}
            <Button onClick={handleCancel}>Kembali</Button>
          </Space>
        </Col>
      </Row>

      <Spin spinning={pageLoading}>
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          disabled={!isEditing}
        >
          {/* BARIS 1: No SJ & Tanggal */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>No Surat Jalan</Text>}
                name="delivery_number"
                rules={[
                  { required: true, message: "No Surat Jalan wajib diisi" },
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>Tanggal Surat Jalan</Text>}
                name="delivery_date"
                rules={[
                  {
                    required: true,
                    message: "Tanggal Surat Jalan wajib diisi",
                  },
                ]}
              >
                <DatePicker style={{ width: "100%" }} format="DD MMMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          {/* BARIS 2: Jenis Dok & No Dok */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>Jenis Dokumen Pabean</Text>}
                name="id_document"
              >
                <Select
                  placeholder="Jenis Dokumen Pabean"
                  options={DOC_OPTIONS}
                  onChange={handleJenisDokumenChange}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>No Dokumen Pabean</Text>}
                name="document_number"
                rules={[
                  { required: true, message: "No dokumen wajib dipilih" },
                ]}
              >
                {/* Kalau sedang edit & loading nopen, tampilkan select/input */}
                {isEditing ? (
                  <Select
                    placeholder="Pilih No Dokumen Pabean"
                    loading={loadingNopen}
                    showSearch
                    optionFilterProp="label"
                    onChange={handleNopenChange}
                    // Allow custom input if not found in list (optional)
                  >
                    {nopenOptions.map((opt) => (
                      <Option key={opt.value} value={opt.value}>
                        {opt.label}
                      </Option>
                    ))}
                  </Select>
                ) : (
                  <Input />
                )}
              </Form.Item>
            </Col>
          </Row>

          {/* BARIS 3: Tanggal Dok + Pemilik */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>Tanggal Dokumen Pabean</Text>}
                name="document_date"
              >
                <DatePicker
                  disabled
                  style={{ width: "100%" }}
                  format="DD MMMM YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label={<Text strong>Pemilik</Text>} name="owner_name">
                <Input disabled />
              </Form.Item>
            </Col>
          </Row>

          {/* BARIS 4: No Container + No Plat */}
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>No Kontainer</Text>}
                name="container_number"
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={<Text strong>No Plat Kendaraan</Text>}
                name="plat_number"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {/* TABEL BARANG */}
          <Card
            bodyStyle={{ padding: 0 }}
            style={{
              marginTop: 16,
              borderRadius: 12,
              border: "1px solid #E4E6EF",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "2.5fr 0.6fr 1fr 0.5fr 0.8fr 0.6fr 0.8fr 0.6fr 0.8fr 50px",
                background: "#F3F6F9",
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: 13,
              }}
            >
              <div>NAMA BARANG</div>
              <div>STOK</div>
              <div>NOPEN ASAL</div>
              <div>SERI</div>
              <div>JML KELUAR</div>
              <div>KEMASAN</div>
              <div>JML SATUAN</div>
              <div>SATUAN</div>
              <div>LOT</div>
              <div style={{ textAlign: "center" }}>AKSI</div>
            </div>

            <Form.List name="items">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field) => (
                    <div
                      key={field.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns:
                          "2.5fr 0.6fr 1fr 0.5fr 0.8fr 0.6fr 0.8fr 0.6fr 0.8fr 50px",
                        padding: "8px 16px",
                        borderTop: "1px solid #E4E6EF",
                        alignItems: "center",
                      }}
                    >
                      {/* Nama Barang */}
                      <Form.Item
                        {...field}
                        name={[field.name, "namaBarang"]}
                        fieldKey={[field.fieldKey, "namaBarang"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input readOnly />
                      </Form.Item>

                      {/* Stok Saat Ini */}
                      <Form.Item
                        {...field}
                        name={[field.name, "stokSaatIni"]}
                        fieldKey={[field.fieldKey, "stokSaatIni"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input readOnly />
                      </Form.Item>

                      {/* Nopen Asal */}
                      <Form.Item
                        {...field}
                        name={[field.name, "nopenAsal"]}
                        fieldKey={[field.fieldKey, "nopenAsal"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input readOnly />
                      </Form.Item>

                      {/* Seri */}
                      <Form.Item
                        {...field}
                        name={[field.name, "seri"]}
                        fieldKey={[field.fieldKey, "seri"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input readOnly />
                      </Form.Item>

                      {/* Jumlah Keluar */}
                      <Form.Item
                        {...field}
                        name={[field.name, "jumlahKeluar"]}
                        fieldKey={[field.fieldKey, "jumlahKeluar"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                        rules={[
                          {
                            required: true,
                            message: "Wajib",
                          },
                        ]}
                      >
                        <Input type="number" min={0} />
                      </Form.Item>

                      {/* Kemasan */}
                      <Form.Item
                        {...field}
                        name={[field.name, "kodeJenisKemasan"]}
                        fieldKey={[field.fieldKey, "kodeJenisKemasan"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input placeholder="Kms" />
                      </Form.Item>

                      {/* Jumlah Satuan */}
                      <Form.Item
                        {...field}
                        name={[field.name, "jumlahSatuan"]}
                        fieldKey={[field.fieldKey, "jumlahSatuan"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input type="number" min={0} />
                      </Form.Item>

                      {/* Satuan */}
                      <Form.Item
                        {...field}
                        name={[field.name, "kodeSatuanBarang"]}
                        fieldKey={[field.fieldKey, "kodeSatuanBarang"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                      >
                        <Input placeholder="Sat" />
                      </Form.Item>

                      {/* LOT */}
                      <Form.Item
                        {...field}
                        name={[field.name, "lot"]}
                        fieldKey={[field.fieldKey, "lot"]}
                        style={{ marginBottom: 0, paddingRight: 8 }}
                        rules={[
                          { required: true, message: "Lot wajib" },
                          {
                            pattern: /^[A-S]\.[1-3]\.\d{1,2}$/,
                            message: "Format: Rak.Lantai.Kolom",
                          },
                        ]}
                      >
                        <Input
                          placeholder="Rak.Lantai.Kolom"
                          onBlur={(e) => {
                            if (!isEditing) return;
                            let raw = e.target.value
                              .toUpperCase()
                              .trim()
                              .replace(/\s+/g, "");
                            if (!raw) return;

                            let formatted = raw;

                            // Already valid: A.1.1
                            if (/^[A-S]\.[1-3]\.\d{1,2}$/.test(formatted)) {
                              // OK
                            }
                            // B23 -> B.2.3 (Row + Level + Col)
                            else if (/^[A-S][1-3]\d{1,2}$/.test(formatted)) {
                              const row = formatted[0];
                              const level = formatted[1];
                              const col = formatted.slice(2);
                              formatted = `${row}.${level}.${col}`;
                            }
                            // A.23 -> A.2.3 (Row.Level+Col)
                            else if (/^[A-S]\.[1-3]\d{1,2}$/.test(formatted)) {
                              const row = formatted[0];
                              const level = formatted[2];
                              const col = formatted.slice(3);
                              formatted = `${row}.${level}.${col}`;
                            }
                            // A2.3 -> A.2.3 (Row+Level.Col)
                            else if (/^[A-S][1-3]\.\d{1,2}$/.test(formatted)) {
                              formatted =
                                formatted[0] + "." + formatted.slice(1);
                            }

                            // Update field value
                            const items = form.getFieldValue("items") || [];
                            if (items[field.name]) {
                              items[field.name].lot = formatted;
                              form.setFieldsValue({ items });
                              setTimeout(() => {
                                form.validateFields([
                                  ["items", field.name, "lot"],
                                ]);
                              }, 0);
                            }
                          }}
                        />
                      </Form.Item>

                      <div style={{ textAlign: "center" }}>
                        <Button
                          type="text"
                          icon={<DeleteOutlined style={{ color: "#F64E60" }} />}
                          onClick={() => remove(field.name)}
                          disabled={!isEditing}
                        />
                      </div>

                      {/* Hidden Fields */}
                      <Form.Item name={[field.name, "kodeBarang"]} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, "kodeJenisKemasan"]} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, "id_customer"]} hidden>
                        <Input />
                      </Form.Item>
                      <Form.Item name={[field.name, "nomorAjuAsal"]} hidden>
                        <Input />
                      </Form.Item>
                    </div>
                  ))}

                  {/* Note: Menambah barang baru di edit mode outgoing agak kompleks karena harus pilih dari stok. 
                      Untuk sekarang kita disable tambah barang, user hanya bisa edit qty atau hapus. 
                      Kalau mau tambah, sebaiknya buat outgoing baru atau kita implementasi modal pilih stok nanti.
                  */}
                </>
              )}
            </Form.List>
          </Card>

          {/* TOTAL BARANG KELUAR */}
          <div
            style={{
              marginTop: 12,
              textAlign: "left",
              fontWeight: 600,
            }}
          >
            TOTAL BARANG KELUAR: {totalBarangKeluar}
          </div>

          <Row justify="end" style={{ marginTop: 24 }}>
            {isEditing && (
              <Space>
                <Button onClick={handleCancelEdit}>Batal</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={saving}
                  style={{
                    minWidth: 120,
                    fontWeight: 600,
                  }}
                >
                  Simpan Perubahan
                </Button>
              </Space>
            )}
          </Row>
        </Form>
      </Spin>

      <Modal
        title="Konfirmasi Update Pengeluaran"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Update"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        <p>Apakah Anda yakin ingin menyimpan perubahan data pengeluaran ini?</p>
        <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4 }}>
          <p style={{ margin: 0 }}>
            <strong>No Surat Jalan:</strong> {pendingValues?.delivery_number}
          </p>
          <p style={{ margin: 0 }}>
            <strong>Tanggal:</strong>{" "}
            {pendingValues?.delivery_date
              ? dayjs(pendingValues.delivery_date).format("DD MMMM YYYY")
              : "-"}
          </p>
          <p style={{ margin: 0 }}>
            <strong>No Dokumen:</strong> {pendingValues?.document_number}
          </p>
        </div>

        <div style={{ marginTop: 16 }}>
          <Text strong>Detail Barang Keluar:</Text>
          <div
            style={{
              maxHeight: 200,
              overflowY: "auto",
              marginTop: 8,
              border: "1px solid #f0f0f0",
            }}
          >
            <table
              style={{
                width: "100%",
                fontSize: 12,
                borderCollapse: "collapse",
              }}
            >
              <thead
                style={{ background: "#fafafa", position: "sticky", top: 0 }}
              >
                <tr>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "left",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Nama Barang
                  </th>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "right",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Jml Keluar
                  </th>
                  <th
                    style={{
                      padding: "4px 8px",
                      textAlign: "left",
                      borderBottom: "1px solid #f0f0f0",
                    }}
                  >
                    Lot
                  </th>
                </tr>
              </thead>
              <tbody>
                {pendingValues?.items
                  ?.filter((i) => (Number(i.jumlahKeluar) || 0) > 0)
                  .map((item, idx) => (
                    <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <td style={{ padding: "4px 8px" }}>{item.namaBarang}</td>
                      <td style={{ padding: "4px 8px", textAlign: "right" }}>
                        {item.jumlahKeluar}
                      </td>
                      <td style={{ padding: "4px 8px" }}>{item.lot}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </Modal>
    </Card>
  );
}
